/* Projects data model + validation — designs/projects-page.md D2/D8.
   validateProjectInput is the one rule set: the server is the authority,
   the client reuses it for instant form feedback. */
import { fmtMonth, MONTH_RE } from "./months";
import { ENTERED_SRC, charterReason, copyStamp, type Charter, type CharterInput } from "./charter";
export { ENTERED_SRC } from "./charter";
import type { Role } from "./roles";
import type { Stamp, UserRef } from "./organizer-roles";

/* ONE dated statement of demand (designs/per-month-role-needs.md PN19): from
   `from` through `to` inclusive, the project needs `count` FTE. `to` absent =
   open-ended, which is a real and common record — a project needing 2 FTE
   from August with no known end must not be made to invent one (settled
   decision 2). `from` IS the effective date settled decision 4 asks for; the
   enclosing project revision's stamp carries who recorded it and when (PN9).

   Supersedes the baseline-plus-changes shape of PN1. Trevor, Aug 28 2026:
   "Whats the top FTE do? Maybe we should require that every demand insert
   requires a date range." Two ways to express one fact, one of them with
   invisible semantics, was the confusion. */
export interface DemandEntry {
  from: string;
  to?: string;
  /* zero is a real statement — "we deliberately need nobody in these months"
     — and is NOT the same as a month no entry covers (PN19) */
  count: number;
}

export interface RoleNeed {
  roleId: string;
  /* the record: dated entries in month order, never overlapping (PN19) */
  demand?: DemandEntry[];
  /* LEGACY, read at need and never written again (PN20). A bare decimal FTE
     in steps of 0.1 (A10) from before dated entries existed; it applies to
     EVERY month, unbounded, which is exactly what its flat demand line meant.
     Mapped at read, never rewritten (the M4 pattern); the editor's first save
     converts the record to entries and drops it. */
  count?: number;
}

/* What the project needs of this role in this month — or NULL, meaning no
   demand is recorded for that month (PN19). The ONE reading: the chart, the
   slot row and the allocation cap all call it, or they disagree.

   null is not zero and must never be rendered as zero. A gap is the absence
   of an answer; `count: 0` is an answer. */
export function needAt(need: RoleNeed, month: string): number | null {
  if (need.demand) {
    const covering = need.demand.find(
      (e) => e.from <= month && (e.to === undefined || month <= e.to),
    );
    return covering ? covering.count : null;
  }
  /* no entries at all: the legacy count, unbounded, or nothing recorded */
  return need.count ?? null;
}

/* At most one charge code in effect per project (charge-codes C1);
   unconfirmed marks an orange assumption — never a fabricated value */
export interface ChargeCodeInput {
  code: string;
  unconfirmed: boolean;
}

export interface ChargeCode extends ChargeCodeInput {
  /* the code's own stamp — carried forward across unrelated edits (C3) so
     the visible effective date is when the code took effect; fresh stamps
     are attributed (AA4) */
  recorded: Stamp;
}

/* A span date the caller supplies (designs/project-span.md PS1): a month at
   the same "YYYY-MM" grain as allocations, plus the unconfirmed flag that
   makes it an orange assumption rather than a confirmed value. */
export interface MonthValue {
  month: string;
  unconfirmed: boolean;
}

/* As served: the date carries its own stamp (PS2) — when the span became
   true, not when the row was last touched. */
export interface SpanDate extends MonthValue {
  recorded: Stamp;
}

/* Same horizon monthly-allocations M2 puts on allocation months: a junk
   far-future span would otherwise define a window thousands of months wide
   (PS3). A limit on absurdity, not on planning. */
const SPAN_FLOOR = "2000-01";
const SPAN_CEIL = "2100-12";

export interface ProjectInput {
  name: string;
  roles: RoleNeed[];
  /* the ASI Project Charter fields (designs/charter-form.md CH2): absent, or
     an absent key inside, means KEEP; an explicit null clears THAT field.
     A null charter as a whole is refused (CH4). */
  charter?: CharterInput;
  /* absent/null = not recorded (red) */
  chargeCode?: ChargeCodeInput | null;
  /* the folder the record is filed in; absent/null = honestly Not filed
     (designs/directories.md DIR3) */
  directoryId?: string | null;
  /* the project's planned span (PS1); absent/null = not recorded (red).
     A start with no end is a legitimate record, not an error. */
  startMonth?: MonthValue | null;
  endMonth?: MonthValue | null;
}

export interface Project extends ProjectInput {
  id: string;
  /* served with the code's own stamp; null = not recorded */
  chargeCode: ChargeCode | null;
  /* always materialized by the stores: null = Not filed (DIR3) */
  directoryId: string | null;
  /* draft = not yet a commitment; archived projects stay in the record —
     delete is for mistakes only */
  recordState: RecordState;
  /* each span date served with its own stamp; null = not recorded (PS1) */
  startMonth: SpanDate | null;
  endMonth: SpanDate | null;
  /* every charter field present, each with its own stamp or null = not
     recorded (charter-form CH2) */
  charter: Charter;
  /* effective-dating stamp (settled decision 4), attributed to the acting
     user since authorization-and-attribution.md AA4 */
  recorded: Stamp;
}

/* Record state — where a project stands in the record (designs/
   project-record-states.md R1). Distinct from the template's "lifecycle
   stage" (Product | Prototype). Listed in pipeline order (section-navigator
   N6): draft first because every project starts there, archived last. */
export const RECORD_STATES = ["draft", "review", "active", "archived"] as const;
export type RecordState = (typeof RECORD_STATES)[number];



/* The charge code keeps its own stamp (designs/charge-codes.md C3): an
   unchanged code carries its previous stamp forward, so renaming a project
   never fakes the code's effective date; a new or changed code is stamped
   now. Used by every store implementation. */
export function chargeCodeRevision(
  input: ChargeCodeInput | null | undefined,
  previous: ChargeCode | null,
  by?: UserRef,
): ChargeCode | null {
  if (!input) return null;
  /* trimmed: whitespace from a raw API caller must not fake a change */
  const code = input.code.trim();
  if (previous && previous.code === code && previous.unconfirmed === input.unconfirmed)
    /* a copy, so append-only history never shares a mutable stamp — the
       original actor stays on the original stamp (AA4) */
    return { ...previous, recorded: copyStamp(previous.recorded) };
  return {
    code,
    unconfirmed: input.unconfirmed,
    recorded: { source: ENTERED_SRC, date: new Date().toISOString(), ...(by ? { by } : {}) },
  };
}

/* A span date keeps its own stamp (designs/project-span.md PS2), exactly as
   the charge code does (charge-codes C3): an unchanged month carries its
   previous stamp forward as a COPY — so append-only history never shares a
   mutable stamp and the original actor stays on the original stamp (AA4) —
   while a new or changed value is stamped now. Renaming a project must never
   re-date when its span became true. */
export function spanDateRevision(
  input: MonthValue | null | undefined,
  previous: SpanDate | null,
  by?: UserRef,
): SpanDate | null {
  if (!input) return null;
  if (previous && previous.month === input.month && previous.unconfirmed === input.unconfirmed)
    return { ...previous, recorded: copyStamp(previous.recorded) };
  return {
    month: input.month,
    unconfirmed: input.unconfirmed,
    recorded: { source: ENTERED_SRC, date: new Date().toISOString(), ...(by ? { by } : {}) },
  };
}

/* Shape + horizon check for one span date (PS3). Returns a reason naming
   WHICH date, so a rejection is actionable without guessing. */
function spanDateReason(value: unknown, which: "start" | "end"): string | null {
  if (value === undefined || value === null) return null;
  if (typeof value !== "object" || Array.isArray(value))
    return `a project's ${which} month must be an object with month and unconfirmed`;
  const { month, unconfirmed } = value as Partial<MonthValue>;
  if (typeof unconfirmed !== "boolean")
    return `the ${which} month's unconfirmed flag must be true or false`;
  if (typeof month !== "string" || !MONTH_RE.test(month))
    return `a project's ${which} month must look like 2026-09`;
  if (month < SPAN_FLOOR || month > SPAN_CEIL)
    return `a project's ${which} month must fall between ${SPAN_FLOOR} and ${SPAN_CEIL}`;
  return null;
}

/* Is this month outside the project's recorded span? Returns the reason to
   SHOW, never a reason to refuse (designs/project-span.md PS6): a stale end
   date must not stop a project manager staffing real work. Nothing recorded
   means nothing to be outside of. Each edge is independent — with only a
   start recorded, only an earlier month is out. */
export function spanPhrase(
  project: Pick<Project, "startMonth" | "endMonth">,
): string | null {
  const { startMonth, endMonth } = project;
  /* An assumption is labelled as one, so nothing built on this ever reads more
     certain than the record behind it (settled decision 2) — and it names
     WHICH date is the assumption, so a confirmed edge is never tarred with the
     other's uncertainty (review finding, Aug 26 2026). */
  const which = [
    ...(startMonth?.unconfirmed ? ["start"] : []),
    ...(endMonth?.unconfirmed ? ["end"] : []),
  ];
  const both = startMonth && endMonth;
  const tail = which.length === 0
    ? ""
    : both
      ? ` (${which.length === 2 ? "both" : which[0]} unconfirmed)`
      : " (unconfirmed)";
  if (both) return `runs ${fmtMonth(startMonth.month)} – ${fmtMonth(endMonth.month)}${tail}`;
  if (startMonth) return `starts ${fmtMonth(startMonth.month)}${tail}`;
  if (endMonth) return `ends ${fmtMonth(endMonth.month)}${tail}`;
  return null;
}

export function outOfSpan(
  project: Pick<Project, "startMonth" | "endMonth">,
  month: string,
): string | null {
  const { startMonth, endMonth } = project;
  const before = startMonth && month < startMonth.month;
  const after = endMonth && month > endMonth.month;
  if (!before && !after) return null;
  /* a span exists whenever either edge does, so this is non-null here */
  return `${fmtMonth(month)} is outside this project's span — it ${spanPhrase(project)!}`;
}

/* An FTE count in steps of 0.1 (A10) — the baseline demands more than zero,
   a scheduled change allows zero (PN6), so the floor is the caller's. */
const fteStep = (count: unknown): boolean =>
  typeof count === "number" && Number.isInteger(Math.round(count * 1000) / 100);

/* The schedule's rules (designs/per-month-role-needs.md PN5). Every reason
   names the role: a dozen rows are on screen, and a rejection that doesn't
   say which one is a puzzle. Absent = a steady need, which is always valid. */
/* The demand entries' rules (designs/per-month-role-needs.md PN19). Every
   reason names the role: a rejection that doesn't say which one is a puzzle. */
function demandReason(demand: unknown, role: Role): string | null {
  if (!Array.isArray(demand)) return `${role.name}'s demand must be a list of dated entries`;
  if (demand.length === 0)
    return `${role.name} needs at least one dated demand entry — remove the role instead`;
  /* In month order and NEVER overlapping: two entries covering one month is a
     contradiction the recorder should resolve, not one the app should guess
     past, and it is the invariant that lets needAt take the first match. An
     open-ended entry covers everything after it, so it can only be last. */
  let previousEnd: string | null = null;
  let previousStart = "";
  let open = false;
  for (const entry of demand) {
    if (typeof entry !== "object" || entry === null || Array.isArray(entry))
      return `each of ${role.name}'s demand entries must be an object with from and count`;
    const { from, to, count } = entry as Partial<DemandEntry>;
    if (typeof from !== "string" || !MONTH_RE.test(from))
      return `${role.name}'s demand entries must each start in a month like 2026-09`;
    if (to !== undefined && (typeof to !== "string" || !MONTH_RE.test(to)))
      return `${role.name}'s demand entries must end in a month like 2026-09, or not at all`;
    if (from < SPAN_FLOOR || from > SPAN_CEIL || (to !== undefined && (to < SPAN_FLOOR || to > SPAN_CEIL)))
      return `${role.name}'s demand entries must fall between ${SPAN_FLOOR} and ${SPAN_CEIL}`;
    if (to !== undefined && to < from)
      return `${role.name} has a demand entry ending before it starts`;
    if (!(typeof count === "number" && count >= 0) || !fteStep(count))
      return `${role.name}'s demand entries need an FTE count of zero or more, in steps of 0.1`;
    if (role.cardinality === "one" && count > 1) return `${role.name} takes at most one person`;
    if (from < previousStart) return `${role.name}'s demand entries must be in month order`;
    if (open || (previousEnd !== null && from <= previousEnd))
      return `${role.name} has two demand entries covering ${fmtMonth(from)}`;
    previousStart = from;
    previousEnd = to ?? from;
    open = to === undefined;
  }
  return null;
}

/* `roles` is the RECORDED catalog (designs/canonical-roles.md CR2) — the
   server passes what the store holds, the client what it fetched; neither
   consults a constant, because a constant would be a second source. */
export function validateProjectInput(
  input: unknown,
  catalog: Role[],
  /* the record being edited, when there is one. PS9 keeps an absent span date
     from the stored record, so the order invariant has to be judged on the
     MERGED span — otherwise a partial edit can persist the inverted span PS3
     says is impossible (review finding, Aug 26 2026). */
  current?: Pick<Project, "startMonth" | "endMonth">,
): string | null {
  /* the server feeds this req.body verbatim — shape checks keep malformed
     bodies a 400 reason, never a thrown 500 */
  if (typeof input !== "object" || input === null || Array.isArray(input))
    return "a project object is required";
  const { name, roles, chargeCode, startMonth, endMonth, charter } = input as Partial<ProjectInput>;
  if (typeof name !== "string" || !name.trim()) return "project name is required";
  /* the charter's own rules live with its catalog (charter-form CH4) */
  const charterFault = charterReason(charter);
  if (charterFault) return charterFault;
  if (chargeCode !== undefined && chargeCode !== null) {
    if (typeof chargeCode !== "object" || Array.isArray(chargeCode))
      return "the charge code must be an object with code and unconfirmed";
    if (typeof chargeCode.code !== "string" || !chargeCode.code.trim())
      return "a charge code needs a non-empty code — record nothing instead";
    if (typeof chargeCode.unconfirmed !== "boolean")
      return "the charge code's unconfirmed flag must be true or false";
  }
  const startReason = spanDateReason(startMonth, "start");
  if (startReason) return startReason;
  const endReason = spanDateReason(endMonth, "end");
  if (endReason) return endReason;
  /* Order only means something when BOTH are recorded — a start with no end
     (or an end with no start) is a legitimate record (PS3). "Recorded" means
     recorded AFTER the edit lands: an absent key keeps the stored date (PS9),
     an explicit null clears it. */
  const merged = (
    key: "startMonth" | "endMonth",
    supplied: MonthValue | null | undefined,
  ): { month: string } | null =>
    supplied === undefined ? (current ? current[key] : null) : supplied;
  const effectiveStart = merged("startMonth", startMonth);
  const effectiveEnd = merged("endMonth", endMonth);
  if (effectiveStart && effectiveEnd && effectiveEnd.month < effectiveStart.month)
    return "the end month must not come before the start month";
  if (!Array.isArray(roles)) return "roles must be a list";
  const seen = new Set<string>();
  for (const need of roles) {
    if (typeof need !== "object" || need === null)
      return "each role need must be an object with roleId and count";
    const role = catalog.find((r) => r.id === need.roleId);
    if (!role) return `"${need.roleId}" is not a canonical role`;
    if (seen.has(need.roleId)) return `${role.name} is listed twice`;
    seen.add(need.roleId);
    /* the legacy shape is still accepted so a stale caller is not broken
       (PN20); it is never written by this app any more */
    if (need.count !== undefined) {
      if (!(need.count > 0) || !fteStep(need.count))
        return `${role.name} needs a positive FTE count in steps of 0.1`;
      if (role.cardinality === "one" && need.count > 1)
        return `${role.name} takes at most one person`;
    }
    /* Either dated entries or the legacy bare count — never both, or the
       record has two answers and a reader has to know which wins (PN19). */
    const { demand, count } = need as Partial<RoleNeed>;
    if (demand !== undefined && count !== undefined)
      return `${role.name} records demand either as dated entries or as one count, not both`;
    if (demand !== undefined) {
      const demandFault = demandReason(demand, role);
      if (demandFault) return demandFault;
    } else if (count === undefined) {
      return `${role.name} needs at least one dated demand entry — remove the role instead`;
    }
  }
  return null;
}
